// Block J (UI-22c, 2026-10-11): the Emails page's presentational pieces, on the v2 kit
// (EmailInbox.jsx keeps the data, the handlers and the layout). Tokens only: emails.css.
import React, { useState, useRef } from 'react';
import { useContacts } from '../../contexts/ContactsContext';
import {
  Button, Field, Input, Textarea, Select, Modal, Checkbox, StatusPill, Avatar, Popover, OverflowMenu, useRevealForm,
} from '../../ui';
import { formatDateTime } from '../../utils/formatting';

// ── Accounts: a short name and a pastel per mailbox (the row pill, the avatar) ───────────────
const PROVIDER = {
  gmail: { short: 'Gmail', color: 'pastel-pink' },
  outlook: { short: 'Outlook', color: 'pastel-peach' },
};
const IMAP_COLORS = ['pastel-blue', 'pastel-mint', 'pastel-lilac', 'pastel-yellow'];

export function accountMeta(email, connections = []) {
  if (PROVIDER[email?.provider]) return PROVIDER[email.provider];
  if (email?.provider === 'aidey') return { short: 'Aidey', color: 'pastel-periwinkle' };
  const imap = connections.filter((c) => c.provider === 'imap');
  const i = Math.max(0, imap.findIndex((c) => c.id === email?.connectionId));
  const conn = imap[i];
  const short = conn?.label || (conn?.email ? conn.email.split('@')[1] : null) || 'Mail';
  return { short, color: IMAP_COLORS[i % IMAP_COLORS.length] };
}

export function connectionLabel(conn) {
  if (conn.provider === 'gmail') return 'Gmail';
  if (conn.provider === 'outlook') return 'Outlook';
  return conn.label || conn.email || 'Email account';
}

export const TRIAGE_META = [
  { key: 'urgent', label: 'Urgent', color: 'pastel-coral' },
  { key: 'actionable', label: 'Actionable', color: 'pastel-orange' },
  { key: 'fyi', label: 'FYI', color: 'pastel-blue' },
  { key: 'newsletters', label: 'Newsletters', color: 'pastel-neutral' },
];

export const senderName = (e) => e.fromName || e.fromEmail || e.from || 'Unknown sender';

// HTML to plain text that keeps the line breaks (a forwarded message, a reply quote).
export function htmlToText(html) {
  const marked = String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, '\n');
  let text;
  if (typeof DOMParser !== 'undefined') {
    text = new DOMParser().parseFromString(marked, 'text/html').body.textContent || '';
  } else {
    text = marked.replace(/<[^>]+>/g, '');
  }
  return text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

// ── The left pane: folders, accounts, Aidey folders (a sheet on a phone) ─────────────────────
function NavItem({ active, onClick, label, count, dotColor, title }) {
  return (
    <button type="button" className={`em-nav__item${active ? ' is-active' : ''}`} aria-current={active ? 'true' : undefined}
      onClick={onClick} aria-label={title ? `${title}${count ? ` ${count}` : ''}` : undefined}>
      {dotColor && <span className="em-nav__dot" style={{ '--em-acc': `var(--${dotColor})` }} aria-hidden="true" />}
      <span className="em-nav__title">{label}</span>
      {count > 0 && <span className="em-nav__count">{count}</span>}
    </button>
  );
}

// A name typed in the nav: a new folder, or a new name for one. onSave answers true when the server
// agreed (the form closes), false when it did not (it stays open with what was typed).
function FolderNameForm({ initial = '', inputLabel, saveLabel = 'Add', onSave, onCancel }) {
  const [name, setName] = useState(initial);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const n = name.trim();
    if (!n || saving) return;
    setSaving(true);
    const done = await onSave(n);
    setSaving(false);
    if (done) onCancel();
  };
  return (
    <div className="em-nav__new">
      <Input autoFocus value={name} aria-label={inputLabel} placeholder="Folder name"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onCancel(); }} />
      <div className="em-actions">
        <Button variant="confirm" size="sm" onClick={save} disabled={!name.trim() || saving}>{saving ? 'Saving…' : saveLabel}</Button>
        <Button variant="tertiary" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export const mailboxFolderKey = (connectionId, folderId) => `${connectionId}:${folderId}`;

// Folders (Carla, 2026-10-21): one account's OWN folders (Gmail labels, Outlook and IMAP folders).
// A mailbox that did not answer says so in its own words; it never reads as "no folders".
function MailboxFolderGroup({ mailbox, title, activeKey, onOpen, onCreate, onRename, onDelete }) {
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const folders = Array.isArray(mailbox.folders) ? mailbox.folders : null;
  return (
    <div className="em-nav__group" data-testid={`mailbox-folders-${mailbox.connectionId}`}>
      <span className="em-nav__label" title={mailbox.email}>{title}</span>
      {!folders && <p className="em-nav__error" role="alert">{mailbox.error || 'This mailbox’s folders did not load.'}</p>}
      {folders && folders.length === 0 && !adding && <p className="em-nav__none">No folders yet.</p>}
      {folders && folders.map((f) => (renaming === f.id ? (
        <FolderNameForm key={f.id} initial={f.name} inputLabel={`New name for ${f.name}`} saveLabel="Save"
          onSave={(n) => onRename(mailbox, f, n)} onCancel={() => setRenaming(null)} />
      ) : (
        <div key={f.id} className="em-nav__folder">
          <NavItem label={f.name} title={f.name} count={f.unreadCount || 0} active={activeKey === mailboxFolderKey(mailbox.connectionId, f.id)}
            onClick={() => onOpen(mailbox, f)} />
          <OverflowMenu label={`Folder options: ${f.name}`} title={f.name} buttonClassName="em-icon-btn ui-hit"
            items={[{ label: 'Rename', onSelect: () => setRenaming(f.id) }, { label: 'Delete', onSelect: () => onDelete(mailbox, f), danger: true }]} />
        </div>
      )))}
      {folders && (adding ? (
        <FolderNameForm inputLabel={`New folder name in ${mailbox.email}`} onSave={(n) => onCreate(mailbox, n)} onCancel={() => setAdding(false)} />
      ) : (
        <button type="button" className="em-nav__add" aria-label={`New folder in ${mailbox.email}`} onClick={() => setAdding(true)}>+ New folder</button>
      ))}
    </div>
  );
}

export function FolderNav({
  systemFolders, connections, accountKey, onAccount, aideyFolders, activeAideyFolderId, onAideyFolder,
  onDeleteAideyFolder, onCreateFolder,
  mailboxes = [], mailboxesError = null, onRetryMailboxes, activeMailboxFolderKey = null,
  onMailboxFolder, onCreateMailboxFolder, onRenameMailboxFolder, onDeleteMailboxFolder,
}) {
  const [adding, setAdding] = useState(false);
  // An account's folder group is named by its provider ("Gmail folders") unless two share one.
  const shortOf = (mb) => connectionLabel(mb);
  const titleFor = (mb) => (mailboxes.filter((x) => shortOf(x) === shortOf(mb)).length > 1 ? `Folders · ${mb.email}` : `${shortOf(mb)} folders`);
  return (
    <nav className="em-nav" aria-label="Mail folders">
      <div className="em-nav__group">
        <span className="em-nav__label">Folders</span>
        {systemFolders.map((f) => <NavItem key={f.key} {...f} />)}
      </div>

      {connections.length > 1 && (
        <div className="em-nav__group">
          <span className="em-nav__label">Accounts</span>
          <NavItem label="All accounts" dotColor="ink" active={accountKey === 'all'} onClick={() => onAccount(null)} />
          {connections.map((c) => {
            const meta = accountMeta({ provider: c.provider, connectionId: c.id }, connections);
            const label = connectionLabel(c);
            const key = c.provider === 'imap' ? `imap-${c.id}` : c.provider;
            return <NavItem key={c.id} label={label} title={label} dotColor={meta.color} active={accountKey === key} onClick={() => onAccount(c)} />;
          })}
        </div>
      )}

      {mailboxesError ? (
        <div className="em-nav__group">
          <span className="em-nav__label">Mailbox folders</span>
          <p className="em-nav__error" role="alert">{mailboxesError}</p>
          {onRetryMailboxes && <button type="button" className="em-nav__add" onClick={onRetryMailboxes}>Try again</button>}
        </div>
      ) : mailboxes.map((mb) => (
        <MailboxFolderGroup key={mb.connectionId} mailbox={mb} title={titleFor(mb)} activeKey={activeMailboxFolderKey}
          onOpen={onMailboxFolder} onCreate={onCreateMailboxFolder} onRename={onRenameMailboxFolder} onDelete={onDeleteMailboxFolder} />
      ))}

      <div className="em-nav__group">
        <span className="em-nav__label">Aidey folders</span>
        {aideyFolders.map((f) => (
          <div key={f.id} className="em-nav__folder">
            <NavItem label={f.name} title={f.name} active={activeAideyFolderId === f.id} onClick={() => onAideyFolder(f)} />
            <button type="button" className="em-icon-btn ui-hit" aria-label={`Delete folder ${f.name}`} title="Delete folder"
              onClick={() => onDeleteAideyFolder(f)}>
              <span aria-hidden="true">✕</span>
            </button>
          </div>
        ))}
        {adding ? (
          <FolderNameForm inputLabel="Folder name" onSave={onCreateFolder} onCancel={() => setAdding(false)} />
        ) : (
          <button type="button" className="em-nav__add" onClick={() => setAdding(true)}>+ New folder</button>
        )}
      </div>
    </nav>
  );
}

// ── Triage chips: the AI's categories, as filters ────────────────────────────────────────────
export function TriageChips({ emails, value, onChange }) {
  const counts = Object.fromEntries(TRIAGE_META.map(({ key }) => [key, emails.filter((e) => e.triageCategory === key).length]));
  const shown = TRIAGE_META.filter(({ key }) => counts[key] > 0);
  if (!shown.length) return null;
  return (
    <div className="em-triage" role="group" aria-label="Filter by Aidey's sorting">
      {shown.map(({ key, label, color }) => (
        <button key={key} type="button" aria-pressed={value === key}
          className="ui-chip ui-hit ui-chip--colored" style={{ '--ui-bg': `var(--${color})` }}
          onClick={() => onChange(value === key ? null : key)}>
          {counts[key]} {label}
        </button>
      ))}
      {value && <button type="button" className="ui-chip ui-hit" onClick={() => onChange(null)}>Clear filter</button>}
    </div>
  );
}

// ── One email in the list ────────────────────────────────────────────────────────────────────
export function EmailRow({ email, time, account, open, checked, onToggle, onSelect, onStar, onDelete, onRestore, showStar = true }) {
  const from = senderName(email);
  const triage = TRIAGE_META.find((m) => m.key === email.triageCategory);
  const unread = !email.isRead;
  return (
    <div role="listitem" className={`em-row${unread ? ' is-unread' : ''}${open ? ' is-open' : ''}${checked ? ' is-checked' : ''}`}
      data-testid="email-row">
      {onToggle ? (
        <span className="em-row__check">
          <Checkbox checked={checked} onChange={onToggle} label={<span className="ui-visually-hidden">Select email from {from}</span>} />
        </span>
      ) : <span />}
      <button type="button" className="em-row__open" onClick={() => onSelect(email)} aria-current={open ? 'true' : undefined}
        aria-label={`${unread ? 'Unread. ' : ''}${from}: ${email.subject || '(no subject)'}. ${time}`}>
        <span className="em-row__line">
          <span className="em-dot" data-on={unread ? 'true' : 'false'} aria-hidden="true" />
          <span className="em-row__from">{from}</span>
          <span className="em-row__time">{time}</span>
        </span>
        <span className="em-row__title">{email.subject || '(no subject)'}</span>
        {email.preview && <span className="em-row__preview">{email.preview}</span>}
        <span className="em-row__tags">
          {account && <span className="em-pill" style={{ '--em-acc': `var(--${account.color})` }}>{account.short}</span>}
          {triage && <span className="em-pill" style={{ '--em-acc': `var(--${triage.color})` }}>{triage.label}</span>}
          {email.draftBody && <span className="em-pill" style={{ '--em-acc': 'var(--pastel-periwinkle)' }}>Aidey draft</span>}
          {email.possibleSpam && <span className="em-pill" style={{ '--em-acc': 'var(--pastel-coral)' }}>Possible spam</span>}
        </span>
      </button>
      <span className="em-row__tools">
        {showStar && (
          <button type="button" className={`em-icon-btn ui-hit em-star${email.isStarred ? ' is-on' : ''}`}
            aria-label={`${email.isStarred ? 'Unstar' : 'Star'} ${email.subject || 'email'}`} aria-pressed={!!email.isStarred}
            onClick={() => onStar(email)}>
            <span aria-hidden="true">{email.isStarred ? '★' : '☆'}</span>
          </button>
        )}
        {onRestore ? (
          <button type="button" className="em-icon-btn ui-hit" aria-label={`Restore to Inbox: ${email.subject || 'email'}`} onClick={() => onRestore([email])}>
            <span aria-hidden="true">↩</span>
          </button>
        ) : onDelete ? (
          <button type="button" className="em-icon-btn ui-hit" aria-label={`Delete email from ${from}`} onClick={() => onDelete([email])}>
            <span aria-hidden="true">🗑</span>
          </button>
        ) : null}
      </span>
    </div>
  );
}

// A spam row: Not spam, Confirm spam.
export function SpamRow({ email, time, onNotSpam, onConfirm }) {
  return (
    <div role="listitem" className={`em-row em-row--spam${email.spamActioned ? ' is-done' : ''}`}>
      <span />
      <div className="em-row__open em-row__open--static">
        <span className="em-row__line">
          <span className="em-row__from">{senderName(email)}</span>
          <span className="em-row__time">{time}</span>
        </span>
        <span className="em-row__title">{email.subject || '(no subject)'}</span>
        {email.preview && <span className="em-row__preview">{email.preview}</span>}
        {/* 2026-10-22: why Aidey moved it, in words (never the email's text). */}
        {email.spamReason && <span className="em-row__reason">{email.spamReason}</span>}
        <span className="em-actions">
          <Button variant="secondary" size="sm" onClick={() => onNotSpam(email)}>Not spam</Button>
          {!email.spamActioned && <Button variant="destructive" size="sm" onClick={() => onConfirm(email)}>Confirm spam</Button>}
        </span>
      </div>
      <span />
    </div>
  );
}

// An Aidey draft in the Drafts folder.
export function AideyDraftRow({ draft, time, open, onSelect }) {
  return (
    <div role="listitem" className={`em-row${open ? ' is-open' : ''}`}>
      <span />
      <button type="button" className="em-row__open" onClick={() => onSelect(draft)} aria-label={`Aidey draft: ${draft.subject || '(no subject)'}. ${time}`}>
        <span className="em-row__line">
          <span className="em-row__title em-row__title--lead">{draft.subject || '(no subject)'}</span>
          <span className="em-row__time">{time}</span>
        </span>
        {draft.preview && <span className="em-row__preview">{draft.preview}</span>}
        <span className="em-row__tags"><span className="em-pill" style={{ '--em-acc': 'var(--pastel-periwinkle)' }}>Aidey draft</span></span>
      </button>
      <span />
    </div>
  );
}

// ── The CRM card under the sender ────────────────────────────────────────────────────────────
export function ContactCard({ fromEmail, fromName, onAddToCrm }) {
  const { contacts } = useContacts();
  const contact = (contacts || []).find((c) => c.email?.toLowerCase() === fromEmail?.toLowerCase());
  if (!contact) {
    return (
      <div className="em-contact">
        <span className="em-contact__text">{fromName || fromEmail} is not in your contacts.</span>
        <Button variant="secondary" size="sm" onClick={onAddToCrm}>+ Add to CRM</Button>
      </div>
    );
  }
  const tone = contact.healthScore >= 70 ? 'success' : contact.healthScore >= 40 ? 'pending' : 'warning';
  return (
    <div className="em-contact">
      <span className="em-contact__who">
        {/* The sender opens their contact pop-up (Carla, 2026-10-21): the same deep link a search or a
            notification uses, so its Email, Book call and Invoice behave as they do on Contacts. */}
        <button type="button" className="em-contact__name em-contact__open" data-testid="email-sender-contact"
          onClick={() => window.dispatchEvent(new CustomEvent('aidey:navigate', { detail: `/contacts?contact=${encodeURIComponent(contact.id)}` }))}>
          {contact.name}
        </button>
        {contact.company && <span className="em-contact__text">{contact.company}</span>}
      </span>
      <span className="em-actions">
        {contact.healthScore != null && <StatusPill tone={tone}>Health {contact.healthScore}</StatusPill>}
        {contact.lastContactedAt && (
          <span className="em-contact__text">Last contact {formatDateTime(contact.lastContactedAt, undefined, { hour: undefined, minute: undefined })}</span>
        )}
      </span>
    </div>
  );
}

// ── Automation for one sender (a popover; a sheet on a phone) ────────────────────────────────
const INTENT_LABEL = {
  meeting_request: 'Meeting requests', invoice_query: 'Invoice queries', po_submission: 'Purchase orders',
  business_enquiry: 'Business enquiries', spam_scammer: 'Spam',
};
export function SenderAutomation({ email, configs, onSet }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const enabled = Object.entries(configs).filter(([, c]) => c?.enabled);
  if (!enabled.length) return null;
  const who = email.fromName || email.fromEmail;
  return (
    <span className="em-menu-anchor">
      <Button ref={ref} variant="tertiary" size="sm" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span aria-hidden="true">⚙</span> Automation
      </Button>
      <Popover anchorRef={ref} open={open} onClose={() => setOpen(false)} title={`Automation for ${who}`} align="left" label={`Automation for ${who}`}>
        <div className="em-auto">
          {enabled.map(([intent]) => (
            <div key={intent} className="em-auto__row">
              <span className="em-auto__label">{INTENT_LABEL[intent] || intent}</span>
              <span className="em-actions">
                <Button variant="tertiary" size="sm" onClick={() => { setOpen(false); onSet(intent, false); }}>Never automate</Button>
                <Button variant="tertiary" size="sm" onClick={() => { setOpen(false); onSet(intent, true); }}>Always auto-send</Button>
              </span>
            </div>
          ))}
        </div>
      </Popover>
    </span>
  );
}

// ── Aidey's drafted reply (the inbox triage drafts one for urgent and actionable mail) ────────
export function AideyDraftCard({ email, onSend, onDismiss, sending }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(email.draftBody || '');
  const to = email.fromEmail || email.from || '';
  const subject = email.draftSubject || (email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject || ''}`);
  const textareaRef = useRef(null);
  return (
    <section className="em-aidey" aria-label="Aidey drafted a reply">
      <button type="button" className="em-aidey__bar" aria-expanded={expanded} onClick={() => setExpanded((x) => !x)}>
        <span className="em-aidey__label">Aidey drafted a reply</span>
        <span className="em-aidey__toggle">{expanded ? 'Hide' : 'Review'}</span>
      </button>
      {expanded && (
        <div className="em-aidey__body">
          <p className="em-muted">To {to} · {subject}</p>
          <Textarea ref={textareaRef} value={body} rows={6} readOnly={!editing} aria-label="Drafted reply"
            onChange={(e) => setBody(e.target.value)} />
          <div className="em-actions">
            <Button variant="confirm" onClick={() => onSend({ to, subject, body, email })} disabled={sending || !body.trim()}>
              {sending ? 'Sending…' : 'Send'}
            </Button>
            {!editing && (
              <Button variant="secondary" onClick={() => { setEditing(true); setTimeout(() => textareaRef.current?.focus(), 40); }}>Edit &amp; send</Button>
            )}
            <Button variant="tertiary" onClick={onDismiss}>Dismiss</Button>
          </div>
        </div>
      )}
    </section>
  );
}

// ── The Aidey summary (decision 5): one sentence from the sorting pass, at most one action ─────
// The summary is a model's words about an email someone outside wrote, so it is OUTSIDE TEXT:
// rendered as a text node only (never HTML, never linkified), and nothing in it acts. The button
// comes from a fixed list the server already validated, is checked again here, and every action
// is an existing, permission-checked path the owner presses:
//   unsubscribe      the email's OWN https List-Unsubscribe URL, from its header, in a new tab
//   add_to_calendar  the event proposal Aidey found in THIS email, accepted (/api/event-proposals)
//   open_scheduler   the Scheduler view
//   save_to_notes    POST /api/notes
const SUMMARY_ACTIONS = {
  unsubscribe: 'Unsubscribe',
  add_to_calendar: 'Add to calendar',
  open_scheduler: 'Open in Scheduler',
  save_to_notes: 'Save to Notes',
};

// The header's https URL, or null. http, mailto:, javascript: and credentials are refused.
export function safeUnsubscribeUrl(raw) {
  if (typeof raw !== 'string' || !raw) return null;
  try {
    const u = new URL(raw);
    if (u.protocol !== 'https:' || u.username || u.password || !u.hostname) return null;
    return u;
  } catch {
    return null;
  }
}

export function AideySummaryCard({ email, onNavigate, onRun }) {
  const [state, setState] = useState({ busy: false, error: null, done: null, clash: false });
  const summary = typeof email.summary === 'string' ? email.summary.trim() : '';
  if (!summary) return null;

  const unsub = safeUnsubscribeUrl(email.unsubscribeUrl);
  const action = SUMMARY_ACTIONS[email.suggestedAction] && (email.suggestedAction !== 'unsubscribe' || unsub)
    ? email.suggestedAction : null;

  const run = async () => {
    if (action === 'unsubscribe') {
      window.open(unsub.href, '_blank', 'noopener,noreferrer');
      setState({ busy: false, error: null, done: `Opened the unsubscribe page on ${unsub.hostname}.`, clash: false });
      return;
    }
    if (action === 'open_scheduler') { onNavigate('Scheduler'); return; }
    setState({ busy: true, error: null, done: null, clash: false });
    try {
      const outcome = await onRun(action, email);
      setState({ busy: false, error: null, done: outcome.message, clash: !!outcome.clash });
    } catch (err) {
      setState({ busy: false, error: err.message || 'That did not work. Please try again.', done: null, clash: false });
    }
  };

  return (
    <section className="em-summary" aria-label="Aidey summary">
      <span className="em-summary__label" aria-hidden="true">Aidey summary</span>
      <p className="em-summary__text">{summary}</p>
      {action && !state.done && (
        <div className="em-summary__actions">
          <Button variant="secondary" size="sm" onClick={run} disabled={state.busy}>
            {state.busy ? 'Working…' : SUMMARY_ACTIONS[action]}
          </Button>
          {action === 'unsubscribe' && <span className="em-muted em-summary__where">Opens {unsub.hostname}</span>}
        </div>
      )}
      {state.done && (
        <p className="em-summary__done" role="status">
          {state.done}
          {state.clash && <> <Button variant="tertiary" size="sm" onClick={() => onNavigate('Notifications')}>Open Notifications</Button></>}
        </p>
      )}
      {state.error && <p className="em-summary__error" role="alert">{state.error}</p>}
    </section>
  );
}

export function AideyDraftPending() {
  return <p className="em-aidey em-aidey--pending">Aidey is preparing a draft reply…</p>;
}

// ── Reply, inline under the email ────────────────────────────────────────────────────────────
export function ReplyComposer({ email, onSend, onClose, onWriteWithAidey, sending, initialBody = '', initialSubject }) {
  const [to, setTo] = useState(email.fromEmail || email.from || '');
  const [subject, setSubject] = useState(initialSubject || (email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject || ''}`));
  const [body, setBody] = useState(initialBody);
  // Rendered under the message body, often far below the window: bring it into view and put the
  // cursor in the message (Carla, 2026-10-21: Reply looked dead).
  const revealRef = useRevealForm(true);
  return (
    <section ref={revealRef} className="em-reply" aria-label="Reply">
      <div className="em-reply__head">
        <h3 className="em-reply__title">Reply</h3>
        <button type="button" className="em-icon-btn ui-hit" aria-label="Close reply" onClick={onClose}><span aria-hidden="true">✕</span></button>
      </div>
      <Field label="To"><Input value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
      <Field label="Message"><Textarea data-reveal-focus value={body} rows={5} placeholder="Write your reply…" onChange={(e) => setBody(e.target.value)} /></Field>
      <div className="em-actions">
        <Button variant="tertiary" onClick={() => onWriteWithAidey({ to, subject, body, email })}>Write with Aidey</Button>
        <Button variant="tertiary" onClick={onClose}>Cancel</Button>
        <Button variant="confirm" onClick={() => onSend({ to, subject, body, email })} disabled={sending || !to || !body}>
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </section>
  );
}

// ── New email, and Forward (the same form, prefilled) ────────────────────────────────────────
// Both go through /api/email/reply with no messageId: a fresh email from the chosen account.
export function ComposeModal({ connections, onSend, onClose, sending, onImprove, initial = null }) {
  const accounts = connections.filter((c) => ['gmail', 'outlook', 'imap'].includes(c.provider));
  const [fromId, setFromId] = useState(() => {
    const pick = (initial?.fromId != null && accounts.find((a) => a.id === initial.fromId)) || accounts.find((a) => a.isDefault) || accounts[0];
    return pick ? pick.id : null;
  });
  const [to, setTo] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState(initial?.subject || '');
  const [body, setBody] = useState(initial?.body || '');
  const [err, setErr] = useState('');
  const [improving, setImproving] = useState(false);
  const [preImprove, setPreImprove] = useState(null);   // the draft before Improve, for Undo
  const from = accounts.find((a) => a.id === fromId);

  async function improve() {
    if (!body.trim()) { setErr('Write a rough draft first, then Improve.'); return; }
    setImproving(true); setErr('');
    const snapshot = { subject, body };
    try {
      const data = await onImprove({ subject, body, to });
      setPreImprove(snapshot);
      if (data.body) setBody(data.body);
      // Only replace the subject when the sender's was empty or weak.
      const weak = !snapshot.subject.trim() || snapshot.subject.trim().length < 4;
      if (weak && data.subject) setSubject(data.subject);
    } catch (e) {
      setErr(e.message);
    } finally {
      setImproving(false);
    }
  }

  function submit() {
    if (!from) { setErr('Choose an account to send from.'); return; }
    if (!to.trim()) { setErr('Add at least one recipient.'); return; }
    if (!subject.trim()) { setErr('Add a subject.'); return; }
    setErr('');
    onSend({ connection: from, to: to.trim(), cc: cc.trim(), bcc: bcc.trim(), subject: subject.trim(), body });
  }

  return (
    <Modal open onClose={onClose} title={initial?.title || 'New email'} color="pastel-orange" width={600}
      footer={<>
        <Button variant="tertiary" onClick={onClose}>Cancel</Button>
        <Button variant="confirm" onClick={submit} disabled={sending}>{sending ? 'Sending…' : 'Send'}</Button>
      </>}>
      <div className="em-compose">
        <Field label="From">
          <Select value={fromId || ''} onChange={(e) => setFromId(Number(e.target.value))}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{(a.label ? `${a.label}, ` : '') + a.email}{a.isDefault ? ' (default)' : ''}</option>
            ))}
          </Select>
        </Field>
        <Field label="To"><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com, another@example.com" /></Field>
        {!showCcBcc ? (
          <button type="button" className="em-link" onClick={() => setShowCcBcc(true)}>+ Cc and Bcc</button>
        ) : (
          <>
            <Field label="Cc" hint="Separate addresses with commas"><Input value={cc} onChange={(e) => setCc(e.target.value)} /></Field>
            <Field label="Bcc" hint="Separate addresses with commas"><Input value={bcc} onChange={(e) => setBcc(e.target.value)} /></Field>
          </>
        )}
        <Field label="Subject"><Input value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
        <Field label="Message" hint={initial?.note}>
          <Textarea value={body} rows={9} placeholder="Write your message…" onChange={(e) => setBody(e.target.value)} />
        </Field>
        <div className="em-actions">
          <Button variant="tertiary" onClick={improve} disabled={improving}>{improving ? 'Improving…' : 'Improve with Aidey'}</Button>
          {preImprove && (
            <Button variant="tertiary" onClick={() => { setSubject(preImprove.subject); setBody(preImprove.body); setPreImprove(null); }} disabled={improving}>
              Undo
            </Button>
          )}
        </div>
        {err && <p className="ui-field__error" role="alert">{err}</p>}
      </div>
    </Modal>
  );
}

// ── The reading view's sender line ───────────────────────────────────────────────────────────
export function SenderLine({ email, account, when }) {
  const name = senderName(email);
  let line;
  if (email.folder === 'sent') line = `to ${email.to || email.toEmail || ''}`;
  else if (email.provider === 'aidey') line = `draft reply to ${email.to || email.fromEmail || ''}`;
  else line = `${email.fromEmail && email.fromName ? `${email.fromEmail} · ` : ''}to ${email.to || 'you'}`;
  return (
    <div className="em-sender">
      <Avatar name={email.folder === 'sent' ? 'You' : name} size={44} color={account?.color || 'pastel-lilac'} />
      <span className="em-sender__text">
        <span className="em-sender__name">{email.folder === 'sent' ? 'You' : name}</span>
        <span className="em-sender__meta">{line}{when ? ` · ${when}` : ''}</span>
      </span>
    </div>
  );
}
